import { useState } from 'react';
import { useCreateEnrollment, useEnrollableUsers } from '@/api/enrollments';
import { Combobox } from '@/components/shared/combobox';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { CourseOption } from '@/api/instructor-courses';

// Diálogo modal para inscribir un empleado en un curso (permite lote sucesivo).
// Auto-contenido. Extraído de enrollments.tsx por ENG-LOC-01.
export function CreateEnrollmentDialog({
  courses,
  onClose,
}: {
  courses: CourseOption[];
  onClose: () => void;
}) {
  const [selectedCourseId, setSelectedCourseId] = useState<number>(0);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { data: teamRes, isLoading: loadingTeam } = useEnrollableUsers();
  const teamUsers = teamRes?.data ?? [];

  const createMutation = useCreateEnrollment();

  const handleSubmit = () => {
    if (!selectedCourseId || !selectedUserId) return;
    setErrorMessage('');
    setSuccessMessage('');

    createMutation.mutate(
      { user_id: selectedUserId, course_id: selectedCourseId },
      {
        onSuccess: () => {
          setSuccessMessage('Empleado inscrito exitosamente.');
          setSelectedUserId(0);
          // Keep dialog open for batch enrollments
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Error al inscribir al empleado.';
          setErrorMessage(msg);
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-card-foreground">Inscribir Empleado</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Course selector */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Curso</label>
          <Combobox
            value={selectedCourseId ? String(selectedCourseId) : ''}
            onChange={(v) => setSelectedCourseId(v ? Number(v) : 0)}
            options={courses.map((c) => ({
              value: String(c.id),
              label: c.status === 'draft' ? `${c.title} (Borrador)` : c.title,
            }))}
            placeholder="Seleccionar curso..."
            clearable
          />
        </div>

        {/* User selector */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Empleado</label>
          {loadingTeam ? (
            <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              Cargando empleados...
            </div>
          ) : (
            <Combobox
              value={selectedUserId ? String(selectedUserId) : ''}
              onChange={(v) => setSelectedUserId(v ? Number(v) : 0)}
              options={teamUsers.map((u) => ({
                value: String(u.id),
                label: `${u.first_name} ${u.last_name}`,
                sublabel: u.email,
              }))}
              placeholder="Seleccionar empleado..."
              clearable
            />
          )}
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorMessage}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            Cerrar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedCourseId || !selectedUserId || createMutation.isPending}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {createMutation.isPending ? 'Inscribiendo...' : 'Inscribir'}
          </button>
        </div>
      </div>
    </div>
  );
}
